const DEMO_URL_KEY = "ig-vb-demo-url";

/**
 * sessionStorage persistence for the visual-builder demo URL.
 *
 * This is the ONLY module that touches storage for the demo URL — it's the
 * persistence edge. The demo URL otherwise lives in redux (hydrated on builder
 * open, written through on change), so consumers read it via selectors, never
 * from here.
 *
 * The value is org-scoped as `<orgId>|<url>` so a demo URL left over from a
 * different org is never applied to the current one (and is cleaned up on read).
 *
 * Every access is guarded: `sessionStorage` throws outright in partitioned or
 * sandboxed contexts (Safari private browsing, some embedded frames). Losing a
 * demo URL is a nuisance; taking `openBuilder` down with it is not, and this is
 * the only storage the builder touches on open.
 */

/** Read the demo URL persisted for `orgId`, or `null` when none applies. */
export function readDemoUrl(orgId?: string): string | null {
  const stored = readRaw();
  if (!stored) return null;

  // Split on the FIRST separator only. An org id can't contain one, but a URL
  // certainly can (`?q=a|b`), and splitting on every occurrence handed back a
  // silently truncated URL that still looked valid — a different page than the
  // merchant configured, with nothing to indicate it.
  const separator = stored.indexOf("|");
  const url = separator === -1 ? "" : stored.slice(separator + 1);
  if (!url) {
    removeRaw();
    return null;
  }

  // Evict only when the entry demonstrably belongs to a *different* org.
  // Without an orgId we can't attribute it — `openBuilder` reads before the
  // organization has necessarily resolved — and evicting there permanently
  // destroyed the merchant's demo URL on a single early open.
  if (!orgId) return null;
  if (stored.slice(0, separator) !== orgId) {
    removeRaw();
    return null;
  }

  return url;
}

/** Persist `url` as the demo URL for `orgId`. */
export function writeDemoUrl(orgId: string, url: string): void {
  try {
    sessionStorage.setItem(DEMO_URL_KEY, `${orgId}|${url}`);
  } catch {
    // Storage unavailable or full. The demo URL still applies for this session
    // from redux; it just won't survive a reload.
  }
}

/** Remove any persisted demo URL. */
export function clearDemoUrl(): void {
  removeRaw();
}

function readRaw(): string | null {
  try {
    return sessionStorage.getItem(DEMO_URL_KEY);
  } catch {
    return null;
  }
}

function removeRaw(): void {
  try {
    sessionStorage.removeItem(DEMO_URL_KEY);
  } catch {
    // Storage unavailable, so there is nothing persisted to remove.
  }
}
